/**
 * Auth Service — thin wrapper around backend REST auth endpoints.
 *
 * Tokens (ID + Access) are stored in module-level variables (memory only).
 * The refresh token is managed via HttpOnly cookie by the browser.
 */

const API_BASE = '/api/auth';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  expiresAt: number; // Unix timestamp (ms) when access token expires
}

export interface UserProfile {
  userId: string;
  nickname: string;
  avatar: string;
}

export interface RegisterInput {
  email: string;
  password: string;
  nickname: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface MfaChallengeInput {
  session: string;
  totpCode: string;
}

export interface ForgotPasswordInput {
  email: string;
}

export interface ResetPasswordInput {
  email: string;
  code: string;
  newPassword: string;
}

export interface AuthResponse {
  tokens?: AuthTokens;
  user?: UserProfile;
  mfaRequired?: boolean;
  session?: string;
  totpSetupUri?: string;
}

export interface AuthError {
  code: string;
  message: string;
}

// ─── In-memory token storage ────────────────────────────────────────────────

let currentTokens: AuthTokens | null = null;

export function getTokens(): AuthTokens | null {
  return currentTokens;
}

export function setTokens(tokens: AuthTokens | null): void {
  currentTokens = tokens;
}

export function getAccessToken(): string | null {
  return currentTokens?.accessToken ?? null;
}

export function getIdToken(): string | null {
  return currentTokens?.idToken ?? null;
}

export function isTokenExpiringSoon(thresholdMs = 5 * 60 * 1000): boolean {
  if (!currentTokens) return false;
  return currentTokens.expiresAt - Date.now() < thresholdMs;
}

export function clearTokens(): void {
  currentTokens = null;
}

// ─── HTTP helpers ───────────────────────────────────────────────────────────

async function authFetch<T>(
  path: string,
  body?: Record<string, unknown>,
  authenticated = false,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (authenticated && currentTokens) {
    headers['Authorization'] = `Bearer ${currentTokens.accessToken}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    credentials: 'include', // Include HttpOnly cookies (refresh token)
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error: AuthError = await response.json().catch(() => ({
      code: 'UNKNOWN_ERROR',
      message: 'An unexpected error occurred',
    }));
    throw error;
  }

  return response.json() as Promise<T>;
}

// ─── Auth API methods ───────────────────────────────────────────────────────

export async function register(input: RegisterInput): Promise<AuthResponse> {
  const response = await authFetch<AuthResponse>('/register', {
    email: input.email,
    password: input.password,
    nickname: input.nickname,
  });

  if (response.tokens) {
    currentTokens = response.tokens;
  }

  return response;
}

export async function login(input: LoginInput): Promise<AuthResponse> {
  const response = await authFetch<AuthResponse>('/login', {
    email: input.email,
    password: input.password,
  });

  if (response.tokens) {
    currentTokens = response.tokens;
  }

  return response;
}

export async function confirmMfa(input: MfaChallengeInput): Promise<AuthResponse> {
  const response = await authFetch<AuthResponse>('/mfa-challenge', {
    session: input.session,
    totpCode: input.totpCode,
  });

  if (response.tokens) {
    currentTokens = response.tokens;
  }

  return response;
}

export async function forgotPassword(input: ForgotPasswordInput): Promise<void> {
  await authFetch<{ message: string }>('/forgot-password', {
    email: input.email,
  });
}

export async function resetPassword(input: ResetPasswordInput): Promise<void> {
  await authFetch<{ message: string }>('/reset-password', {
    email: input.email,
    code: input.code,
    newPassword: input.newPassword,
  });
}

export async function refresh(): Promise<AuthTokens> {
  // The browser automatically sends the HttpOnly refresh token cookie
  const response = await authFetch<{ tokens: AuthTokens }>('/refresh');
  currentTokens = response.tokens;
  return response.tokens;
}

export async function signOut(): Promise<void> {
  try {
    await authFetch<{ message: string }>('/signout', undefined, true);
  } catch {
    // Swallow sign-out API errors — clearing local state is what matters
  } finally {
    // Always clear in-memory tokens regardless of API response
    currentTokens = null;
  }
}
